import React, {useEffect, useState} from 'react';

import {ChangeRequestMetadataReviewFeedback} from 'src/modules/ai';
import {CONTEXTUAL_LINK_INFO} from 'src/modules/event-create/event-create-hovers';
import {ContextualLink} from 'src/modules/event-create/header-block/contextual-link/contextual-link';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';

import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group} from '@pb/kit-component-group';
import {InfoIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';

type Props = {
    contextualLinks: ChangeRequestContextualLink[],
    validationHighLight: boolean,
    changeRequestId: number | null,
    onChangeLink: (value: ChangeRequestContextualLink[]) => void,
};

const LINK_LIMIT = 5;
const LINKS_FIELD_NAME = 'contextualLinks';

export const ContextualLinkController = (props: Props) => {
    const {contextualLinks, validationHighLight, changeRequestId, onChangeLink} = props;
    const [addedLinks, setAddedLinks] = useState<ChangeRequestContextualLink[]>([]);

    useEffect(() => {
        if (contextualLinks) {
            setAddedLinks(contextualLinks);
        }
    }, [contextualLinks]);

    useEffect(() => {
        if (addedLinks) {
            onChangeLink(addedLinks);
        }
    }, [addedLinks, onChangeLink]);

    const matchCondition = (item: ChangeRequestContextualLink, compareLink: ChangeRequestContextualLink) =>
        item.title === compareLink.title && item.value === compareLink.value;

    const getIndex = (links: ChangeRequestContextualLink[], addedLink: ChangeRequestContextualLink) =>
        links.findIndex(item => matchCondition(item, addedLink));

    const updateLink = (updatedLink: ChangeRequestContextualLink, link: ChangeRequestContextualLink) => {
        setAddedLinks(prevLinks => {
            const index = getIndex(prevLinks, link);

            if (index !== -1) {
                const updatedArray = [...prevLinks];
                updatedArray[index] = {...updatedArray[index], title: updatedLink.title, value: updatedLink.value};
                return updatedArray;
            }

            return prevLinks;
        });
    };

    const onChange = (addedLink: ChangeRequestContextualLink, currantLink?: ChangeRequestContextualLink) => {
        if (!addedLink.title || getIndex(addedLinks, addedLink) !== -1) {
            return;
        }

        if (currantLink) {
            updateLink(addedLink, currantLink);
            return;
        }

        setAddedLinks([...addedLinks, addedLink]);
    };

    const onRemove = (linkToDelete?: ChangeRequestContextualLink) => {
        if (linkToDelete) {
            setAddedLinks(prevLinks =>
                prevLinks.filter(item => !matchCondition(item, linkToDelete)));
        }
    };

    const hasValidationError =
        Boolean(validationHighLight && addedLinks.length === 0 && !contextualLinks);

    return (
        <Group direction="v" gap="xxs" justify="space-between">
            <Group gap="m">
                <Group gap="xs" alignItems="center">
                    <strong>Links:</strong>
                    <Dropdown
                        hoverEnabled={true}
                        clickEnabled={false}
                        maxWidth={500}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                        )}>
                        {CONTEXTUAL_LINK_INFO}
                    </Dropdown>
                    <ChangeRequestMetadataReviewFeedback
                        changeRequestId={changeRequestId}
                        fieldName={LINKS_FIELD_NAME}
                    />
                </Group>
                <Tooltip
                    anchor={({anchorRef, getAnchorProps}) => (
                        <div ref={anchorRef} {...getAnchorProps()}>
                            <ContextualLink
                                disabled={addedLinks.length >= LINK_LIMIT}
                                validationHighLight={hasValidationError}
                                onChange={onChange}
                            />
                        </div>
                    )}>
                    Add Link
                </Tooltip>
            </Group>
            <Group direction="v" gap="xxs" justify="space-between">
                {addedLinks.map((link, index) =>
                    <ContextualLink key={index} contextualLink={link} onChange={onChange} onRemove={onRemove}/>,
                )}
            </Group>
        </Group>
    );
};
